import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { MaskedCurrency } from '../MaskedValue';
import { resolveAmountColor } from '../AmountDisplay';
import { SectionContainer } from '../ds';
import { BalanceSkeleton } from '../SkeletonLoader';
import { interpolate } from '../../utils/entryType';
import { summarizeBudgetsByCategory, dashboardCategoryTable } from '../../utils/budgetsByCategory';
import FigureNote from './FigureNote';

// Board columns (#2143): Category · count | Spent | Still available | Budgets over | Includes.
const COLUMNS = '240px 150px 150px 170px minmax(0, 1fr)';

// #2167: a category name is an in-page link to that category's Budgets group
// further down the dashboard (DashboardBudgetGroups marks each group with
// data-dashboard-budget-group). A collapsed group opens first, so the scroll
// never lands on a closed header. The app bar is sticky, so the target sits
// its rendered height (44px on md+, two rows below md) plus a small gap
// below the top of the viewport; scrollIntoView would tuck it underneath.
const SCROLL_GAP_PX = 8;
const scrollToBudgetGroup = (key) => {
  const group = document.querySelector(`[data-dashboard-budget-group="${key}"]`);
  if (!group) return;
  const closedHeader = group.querySelector(':scope > button[aria-expanded="false"]');
  if (closedHeader) closedHeader.click();
  const bar = document.querySelector('header.MuiAppBar-root');
  const barHeight = bar ? bar.getBoundingClientRect().height : 0;
  window.scrollTo({ top: group.getBoundingClientRect().top + window.scrollY - barHeight - SCROLL_GAP_PX, behavior: 'smooth' });
};

// "Oct '26" in the given locale: short month, then an apostrophe and the
// two-digit year, so the title reads as the board has it.
const shortMonthYear = (yearMonth, locale) => {
  const [y, m] = yearMonth.split('-').map(Number);
  const parts = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).formatToParts(new Date(y, m - 1, 1));
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';
  return `${month} '${year}`;
};

const joinNames = (names) => names.join(', ');

/**
 * Month to month budgets (#2143, #2179, epic #2138): the current month's
 * Month to month Normal budgets summed per category, then a Total row over
 * those categories, then Spending outside budgets on its own line, so the
 * unallocated spending stands apart from the budgets. Planning ahead budgets
 * are not on the card. Spent comes from `/budget-months`, Still available
 * from `/tangible` `byBudget`, so the card costs no call of its own beyond
 * the month rows.
 *
 * From md up a table with the board's five columns and a 4 px red / green
 * bar under each category name. Below md one mini card per row: name and
 * count, Spent and Still available side by side under small captions,
 * Budgets over on its own line when any, the bar full width. The Includes
 * lists stay off phones (owner, #2143).
 *
 * Every label-and-value pair explains itself on hover, focus and tap
 * (FigureNote), in words and never with an amount. Section 11 coloring:
 * category rows neutral, the Total row and negatives colored.
 *
 * @param {object} props
 * @param {Array} props.budgets                  active budgets, any type
 * @param {Array} props.budgetCategories
 * @param {Array|null} props.monthRows           `/budget-months` budgets, null while loading
 * @param {boolean} props.error                  the month rows failed to load
 * @param {Array} props.byBudget                 `/tangible` byBudget rows
 * @param {number} props.unbudgetedSpendingCents the month's Spending outside budgets (signed)
 * @param {string} props.yearMonth
 * @param {Object} props.strings                 engine strings
 * @param {string} props.locale
 * @param {('default'|'compact')} [props.density]
 */
const BudgetsByCategory = ({
  budgets, budgetCategories, monthRows, error = false, byBudget, unbudgetedSpendingCents, yearMonth, strings, locale, density = 'default',
}) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const s = (key) => strings[`dashboardN.table.${key}`];
  const title = interpolate(s('title'), { month: shortMonthYear(yearMonth, locale) });

  const dim = { fontSize: `${ds.font.dim}px`, lineHeight: 1.4, color: palette.text.dim };
  const dash = <Box component="span" sx={{ color: palette.text.dim }}>-</Box>;

  const amount = (cents, tone, { neutral = false } = {}) => (
    <MaskedCurrency
      value={cents / 100}
      style={{ color: neutral ? resolveAmountColor(palette, 'row', false, cents === 0) : resolveAmountColor(palette, tone, cents < 0, cents === 0), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: tone === 'total' ? ds.font.weightMedium : ds.font.weightRegular }}
    />
  );
  // Spent is money out, so it keeps the stored sign on every row and reads
  // in accounting parens. Red belongs to the Total row alone (owner, #2179);
  // category rows and the outside line stay neutral, zero dim.
  const spentAmount = (row, tone) => amount(row.spentCents, tone, { neutral: row.kind !== 'total' });

  // Hover text per cell; the pair is the FigureNote target.
  const spentNote = (row) => {
    if (row.kind === 'outside') return s('outside.spent');
    if (row.kind === 'total') return s('total.note');
    return interpolate(s('row.spent'), { count: row.budgetCount });
  };
  const availableNote = (row) => {
    if (row.kind === 'total') return s('total.note');
    const base = interpolate(s('row.available'), { count: row.budgetCount });
    return row.overCount > 0 ? `${base} ${interpolate(s('row.availableOver'), { names: joinNames(row.overNames) })}` : base;
  };
  const overNote = (row) => interpolate(s('row.over'), { names: joinNames(row.overNames) });
  const barNote = (share) => (share >= 1
    ? s('barAllSpent')
    : interpolate(s('bar'), { spent: Math.round(share * 100), available: 100 - Math.round(share * 100) }));

  const pair = (note, children, sx = {}) => (
    <FigureNote note={note} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: `${ds.space[2]}px`, minWidth: 0, ...sx }}>
      {children}
    </FigureNote>
  );

  // Category names are links (section 11 Link: interactive, underline on
  // hover); the Total row and the outside line stay text.
  const nameSx = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const linkSx = {
    ...nameSx, border: 0, background: 'none', padding: 0, font: 'inherit', color: palette.interactive, cursor: 'pointer', textAlign: 'left',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  };
  const nameCell = (row, sx = {}) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: `${ds.space[2]}px`, minWidth: 0, color: palette.text.primary, fontWeight: row.kind === 'total' ? ds.font.weightMedium : ds.font.weightRegular, ...sx }}>
      {row.kind === 'category'
        ? <Box component="button" type="button" data-testid="category-link" onClick={() => scrollToBudgetGroup(row.key)} sx={linkSx}>{row.name}</Box>
        : <Box component="span" sx={nameSx}>{s(row.kind)}</Box>}
      {row.kind === 'category' && (
        <Box component="span" data-testid="category-count" sx={{ fontSize: '13px', color: palette.text.dim, whiteSpace: 'nowrap' }}>{`· ${row.budgetCount}`}</Box>
      )}
    </Box>
  );

  const overCell = (row) => {
    if (row.kind === 'outside' || !row.overCount) return dash;
    return pair(overNote(row), (
      <Box
        component="span"
        data-testid="budgets-over"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: '96px', height: '24px', padding: '0 10px', borderRadius: '12px',
          fontSize: '12px', fontWeight: ds.font.weightMedium, backgroundColor: palette.tint.negative, color: palette.error.main, justifyContent: 'center',
        }}
      >
        {interpolate(s('overCount'), { count: row.overCount })}
        <Box component="span" aria-hidden>·</Box>
        {amount(row.overCents, 'row')}
      </Box>
    ));
  };

  const bar = (row) => (row.kind === 'category' && row.share !== null ? (
    <FigureNote note={barNote(row.share)} data-testid="category-bar" sx={{ display: 'flex', gap: '2px', height: '4px', width: '100%' }}>
      {row.share > 0 && <Box sx={{ flex: `${row.share} 0 0`, height: '4px', borderRadius: '2px', backgroundColor: palette.error.main }} />}
      {row.share < 1 && <Box sx={{ flex: `${1 - row.share} 0 0`, height: '4px', borderRadius: '2px', backgroundColor: palette.success.main }} />}
    </FigureNote>
  ) : null);

  // The Includes column: member names (full list on hover) on category rows,
  // the fixed sentence on the outside line, nothing on the Total row.
  const includesCell = (row) => {
    if (row.kind === 'total') return <Box />;
    const text = row.kind === 'category' ? joinNames(row.includes) : s('outside.includes');
    return pair(text, (
      <Box component="span" sx={{ ...dim, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', pl: `${ds.space[3]}px` }}>{text}</Box>
    ), { display: 'flex' });
  };

  let body;
  if (error) {
    body = <Typography sx={{ ...dim, fontSize: `${ds.font.row}px`, py: `${ds.space[2]}px` }}>{s('error')}</Typography>;
  } else if (!monthRows) {
    body = <Box sx={{ py: `${ds.space[5]}px` }}><BalanceSkeleton width="180px" /></Box>;
  } else {
    const summary = summarizeBudgetsByCategory(budgets, budgetCategories, monthRows, byBudget);
    const table = dashboardCategoryTable(summary, unbudgetedSpendingCents, { default: strings['budgetCategories.default'] });
    const rows = [...table.rows, table.total, table.outside];
    const headerSx = { fontSize: '11px', letterSpacing: ds.font.letterUpper, textTransform: 'uppercase', color: palette.text.dim };
    const rowBorder = `1px solid ${palette.divider}`;

    // The Total row and the outside line sit at the small indent and read as
    // totals (a hairline opens the Total row); category rows sit deeper.
    const tableRow = (row) => {
      const total = row.kind === 'total';
      const tone = total ? 'total' : 'row';
      return (
        <Box
          key={row.key}
          data-testid={`category-row-${row.key}`}
          data-kind={row.kind}
          sx={{
            display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, rowGap: '5px', alignItems: 'center',
            py: `${ds.space[3]}px`, borderBottom: rowBorder, borderTop: total ? rowBorder : 'none',
            fontSize: `${total ? 13.5 : 13}px`,
          }}
        >
          {nameCell(row, { pl: `${row.kind === 'category' ? ds.space[6] : ds.space[3]}px` })}
          <Box sx={{ textAlign: 'right' }}>{pair(spentNote(row), spentAmount(row, tone))}</Box>
          <Box sx={{ textAlign: 'right' }}>{row.kind === 'outside' ? dash : pair(availableNote(row), amount(row.availableCents, tone))}</Box>
          <Box sx={{ textAlign: 'right' }}>{overCell(row)}</Box>
          {includesCell(row)}
          {row.kind === 'category' && <Box sx={{ gridColumn: '1', pl: `${ds.space[10]}px`, pr: `${ds.space[3]}px` }}>{bar(row)}</Box>}
        </Box>
      );
    };

    // Phone cards (#2179): category cards sit one step in from the card
    // edge, the Total and the outside line at the edge, so the summary level
    // reads at a glance; every card shares one two-column grid, Spent flush
    // left and Still available flush right, so the figures line up down the
    // whole card. Names truncate on one line; no Budgets over pill on phones
    // (owner: not needed, visually disruptive).
    const cardRow = (row) => {
      const total = row.kind === 'total';
      const tone = total ? 'total' : 'row';
      const caption = (text, align) => <Box sx={{ ...headerSx, mb: '2px', textAlign: align }}>{text}</Box>;
      return (
        <Box key={row.key} data-testid={`category-row-${row.key}`} data-kind={row.kind} sx={{ py: `${ds.space[4]}px`, pl: row.kind === 'category' ? `${ds.space[4]}px` : 0, borderBottom: rowBorder, borderTop: total ? rowBorder : 'none', fontSize: `${total ? 13.5 : 13}px` }}>
          {nameCell(row, { mb: `${ds.space[2]}px` })}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', columnGap: `${ds.space[5]}px`, alignItems: 'end' }}>
            <Box sx={{ minWidth: 0 }}>{caption(s('colSpent'), 'left')}{pair(spentNote(row), spentAmount(row, tone))}</Box>
            <Box sx={{ minWidth: 0, textAlign: 'right' }}>
              {row.kind !== 'outside' && caption(s('colAvailable'), 'right')}
              {row.kind !== 'outside' && pair(availableNote(row), amount(row.availableCents, tone), { justifyContent: 'flex-end' })}
            </Box>
          </Box>
          {row.kind === 'category' && row.share !== null && <Box sx={{ mt: `${ds.space[3]}px` }}>{bar(row)}</Box>}
        </Box>
      );
    };

    body = (
      <Box data-testid="budgets-by-category" data-layout={isWide ? 'table' : 'cards'}>
        {isWide && (
          <Box data-testid="category-header-row" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, py: `${ds.space[3]}px`, borderBottom: rowBorder, ...headerSx }}>
            <Box sx={{ pl: `${ds.space[3]}px` }}>{pair(s('colCategory.note'), <span>{s('colCategory')}</span>)}</Box>
            <Box sx={{ textAlign: 'right' }}>{pair(s('colSpent.note'), <span>{s('colSpent')}</span>)}</Box>
            <Box sx={{ textAlign: 'right' }}>{pair(s('colAvailable.note'), <span>{s('colAvailable')}</span>)}</Box>
            <Box sx={{ textAlign: 'right' }}>{pair(s('colOver.note'), <span>{s('colOver')}</span>)}</Box>
            <Box sx={{ pl: `${ds.space[3]}px` }}>{s('colIncludes')}</Box>
          </Box>
        )}
        {rows.map((row) => (isWide ? tableRow(row) : cardRow(row)))}
      </Box>
    );
  }

  return (
    <SectionContainer title={title} showDot={false} density={density} aria-label={title} data-testid="budgets-by-category-card">
      {body}
    </SectionContainer>
  );
};

export default BudgetsByCategory;
